import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';

export let changeCertificateMessage = `will overwrite the currently installed certificate.
    Generating the certificate is a lengthy operation during which the service may be affected,
    specifically the SSL/TLS communication.
    Are you sure you want to generate a new self-signed certificate?`;

@Component({
  selector: 'certificate',
  templateUrl: './certificate.component.html',
  styleUrls: ['./certificate.component.scss']
})
export class CertificateComponent implements OnInit {
  @ViewChild('keyStore', {static: true}) keyStoreFilePicker;
  @ViewChild('csr', {static: true}) csrFilePicker;
  @Input() type = 'configurator';

  @Output() onGenerateCertificateSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() onLoadCertificateSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() onViewCurrentCertificate: EventEmitter<any> = new EventEmitter<any>();


  certificate: any = {
    keyAlg: 'RSA',
    keySize: 2048,
    signAlg: 'SHA256withRSA',
    generateType: 'privateKeyAndSelfSigned',
    validity: 365
  };

  uploadCertificate: any = {};

  keyAlgorithmOptions = [
    {text: 'RSA', value: 'RSA'},
    {text: 'EC', value: 'EC'}
  ];

  keySizeRsaOptions = [
    {text: '2048', value: 2048},
    {text: '3072', value: 3072},
    {text: '4096', value: 4096}
  ];
  keySizeEcOptions = [
    {text: '256', value: 256},
    {text: '384', value: 384},
    {text: '521', value: 521}
  ];

  signatureAlgorithmRsaOptions = [
    {text: 'SHA256withRSA', value: 'SHA256withRSA'},
    {text: 'SHA384withRSA', value: 'SHA384withRSA'},
    {text: 'SHA512withRSA', value: 'SHA512withRSA'}
  ];
  signatureAlgorithmEcOptions = [
    {text: 'SHA256withECDSA', value: 'SHA256withECDSA'},
    {text: 'SHA384withECDSA', value: 'SHA384withECDSA'},
    {text: 'SHA512withECDSA', value: 'SHA512withECDSA'}
  ];

  extendedKeyUsageOptions = [
    {text: 'anyExtendedKeyUsage', value: 'anyExtendedKeyUsage'},
    {text: 'serverAuth', value: 'serverAuth'},
    {text: 'clientAuth', value: 'clientAuth'},
    {text: 'codeSigning', value: 'codeSigning'},
    {text: 'emailProtection', value: 'emailProtection'},
    {text: 'timeStamping', value: 'timeStamping'},
    {text: 'OCSPSigning', value: 'OCSPSigning'}
  ];
  keyUsageOptions = [
    {text: 'digitalSignature', value: 'digitalSignature'},
    {text: 'nonRepudiation', value: 'nonRepudiation'},
    {text: 'keyEncipherment', value: 'keyEncipherment'},
    {text: 'dataEncipherment', value: 'dataEncipherment'},
    {text: 'keyAgreement', value: 'keyAgreement'},
    {text: 'keyCertSign', value: 'keyCertSign'},
    {text: 'cRLSign', value: 'cRLSign'},
    {text: 'encipherOnly', value: 'encipherOnly'},
    {text: 'decipherOnly', value: 'decipherOnly'}
  ];

  keySizeOptions = this.keySizeRsaOptions;
  signatureAlgorithmOptions = this.signatureAlgorithmRsaOptions;
  canSubmit: boolean;

  constructor(private dialog: MatDialog,
              private userPrivilege: UserPrivilegeService) {
    this.canSubmit = userPrivilege.isAuthorized();
  }

  ngOnInit(): void {

  }

  submit(): void {
    const certificateToUpdate = _.cloneDeep(this.certificate);

    if (!_.isNil(certificateToUpdate.sanEmail)) {
      certificateToUpdate.sanEmail = certificateToUpdate.sanEmail.map(item => item.value);
    }

    if (!_.isNil(certificateToUpdate.sanURI)) {
      certificateToUpdate.sanURI = certificateToUpdate.sanURI.map(item => item.value);
    }

    if (!_.isNil(certificateToUpdate.sanDNS)) {
      certificateToUpdate.sanDNS = certificateToUpdate.sanDNS.map(item => item.value);
    }

    if (!_.isNil(certificateToUpdate.sanIP)) {
      certificateToUpdate.sanIP = certificateToUpdate.sanIP.map(item => item.value);
    }

    this.onGenerateCertificateSubmit.emit(certificateToUpdate);
  }

  onKeyAlgorithmChange(keyAlgorithm): void {
    if (keyAlgorithm === 'RSA') {
      this.keySizeOptions = this.keySizeRsaOptions;
      this.signatureAlgorithmOptions = this.signatureAlgorithmRsaOptions;
      this.certificate.keySize = 2048;
      this.certificate.signAlg = 'SHA256withRSA';
    } else {
      this.keySizeOptions = this.keySizeEcOptions;
      this.signatureAlgorithmOptions = this.signatureAlgorithmEcOptions;
      this.certificate.keySize = 256;
      this.certificate.signAlg = 'SHA256withECDSA';
    }
  }

  viewCurrentCertificate(): void {
    this.onViewCurrentCertificate.emit();
  }

  onKeyStoreFileChange(files: any): void {
    this.uploadCertificate.keyStoreFile = (files && files.length === 1) ? files[0] : null;

    // clear
    if (this.uploadCertificate.keyStoreFile == null){
      this.uploadCertificate.passwordKS = null;
      this.uploadCertificate.passwordPfx = null;
    }
  }

  onCsrFileChange(files: any): void {
    this.uploadCertificate.csrfile = (files && files.length === 1) ? files[0] : null;
  }

  uploadCertificateFiles(): void {
    const configurationTomcatNote = '\n\nNote: The password has been provided; the Tomcat will automatically ' +
      'be restarted for the KeyStore change to take effect.';

    const serverTomcatNote = '\n\nNote: The password has been provided; the Routing Server\'s Tomcat will automatically ' +
      'be restarted for the KeyStore change to take effect.';

    let confirmationMessage = `Uploading a KeyStore ${changeCertificateMessage}`;

    if (this.uploadCertificate.passwordKS || this.uploadCertificate.passwordPfx) {
      confirmationMessage += `${this.type === 'configurator' ?  configurationTomcatNote : serverTomcatNote}`;
    }

    this.uploadCertificate.password = this.uploadCertificate.passwordKS || this.uploadCertificate.passwordPfx;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '460px',
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      dialogRef.close();
      this.uploadFiles();
    });
  }

  resetCertificate(): void {
    this.certificate = {
      keyAlg: 'RSA',
      keySize: 2048,
      signAlg: 'SHA256withRSA',
      generateType: 'privateKeyAndSelfSigned',
      validity: 365
    };
    this.uploadCertificate = {};
   // this.keyStoreFilePicker.clearFileName();
   // this.csrFilePicker.clearFileName();
  }

  private uploadFiles(): void {
    this.onLoadCertificateSubmit.emit(this.uploadCertificate);
  }
}
